import { useCallback, useEffect, useRef, useState } from "react";
import { ZoomMtg } from "@zoom/meetingsdk";
import type { ZoomClient } from "./zoomClient";
import { createZoomClient, destroyZoomClient } from "./zoomClient";
import {
  ZoomShellStatus,
  createZoomShellEnvelope,
  isZoomShellCommand,
  assertUnreachable,
} from "@repo/zoom-shared";
import type {
  ZoomJoinMeetingCommandPayload,
  ZoomShellCommandEnvelope,
  ZoomShellConfigurationPayload,
  ZoomShellStatusPayload,
} from "@repo/zoom-shared";

// Define proper types for Zoom SDK event payloads
interface ZoomUserLeavePayload {
  userId?: string;
  reason?: number;
  // Additional properties that may be included
  [key: string]: unknown;
}

interface ZoomAttendeeLeavePayload {
  // Attendee leave payload structure
  [key: string]: unknown;
}

interface ZoomMeetingEventPayload {
  event?: string;
  type?: string;
  // Additional properties that may be included
  [key: string]: unknown;
}

interface ZoomGetCurrentUserPayload {
  userId?: string;
  // Additional user properties
  [key: string]: unknown;
}

// Extend the ZoomMtg type to support additional events that are available at runtime
declare module "@zoom/meetingsdk" {
  namespace ZoomMtg {
    function inMeetingServiceListener(
      event: "onUserLeave",
      callback: (payload: ZoomUserLeavePayload) => void
    ): void;
    function inMeetingServiceListener(
      event: "onAttendeeLeave",
      callback: (payload: ZoomAttendeeLeavePayload) => void
    ): void;
    function inMeetingServiceListener(
      event: "onMeetingEvent",
      callback: (payload: ZoomMeetingEventPayload) => void
    ): void;
  }
}

const STATUS_COPY: Record<ZoomShellStatus, string> = {
  initializing: "Initializing Zoom Meeting SDK...",
  ready: "Ready to receive join commands",
  joining: "Joining the meeting...",
  joined: "Meeting in progress",
  "in-meeting": "Meeting in progress",
  leaving: "Leaving the meeting...",
  left: "Meeting ended",
  error: "Unable to load Zoom client",
};

const ZOOM_MEETING_STATUS_MAP: Record<number, ZoomShellStatus> = {
  1: "joining", // connecting
  2: "in-meeting", // connected
  3: "left", // disconnected
  4: "joining", // reconnecting
};

const ZOOM_MEETING_STATUS_TEXT_MAP: Record<string, ZoomShellStatus> = {
  connecting: "joining",
  reconnecting: "joining",
  waitinghost: "joining",
  waiting: "joining",
  connected: "in-meeting",
  inmeeting: "in-meeting",
  inprogress: "in-meeting",
  disconnected: "left",
  ended: "left",
  left: "left",
  failed: "error",
};

const mapMeetingStatus = (value: unknown): ZoomShellStatus | null => {
  if (typeof value === "number" && !Number.isNaN(value)) {
    return ZOOM_MEETING_STATUS_MAP[value] ?? null;
  }

  if (typeof value === "string") {
    const trimmed = value.trim();

    if (trimmed.length === 0) {
      return null;
    }

    const maybeNumber = Number(trimmed);

    if (!Number.isNaN(maybeNumber)) {
      return ZOOM_MEETING_STATUS_MAP[maybeNumber] ?? null;
    }

    const normalized = trimmed.toLowerCase().replace(/[^a-z]/g, "");

    if (!normalized) {
      return null;
    }

    return ZOOM_MEETING_STATUS_TEXT_MAP[normalized] ?? null;
  }

  return null;
};

const computeReferrerOrigin = (): string => {
  try {
    if (typeof document !== "undefined" && document.referrer) {
      return new URL(document.referrer).origin;
    }
  } catch {
    // ignore parse issues and fall back to wildcard
  }

  if (typeof window !== "undefined") {
    return window.location.origin;
  }

  return "*";
};

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === "string" && value.trim().length > 0;

const normalizeZoomError = (error: unknown): string => {
  try {
    if (!error) {
      return "Unknown Zoom Meeting SDK error.";
    }

    if (typeof error === "string") {
      // Handle string errors safely
      try {
        return error.trim() || "Empty error message received.";
      } catch (trimError) {
        console.error("Error trimming error string:", trimError);
        return "Error message processing failed.";
      }
    }

    if (error instanceof Error) {
      try {
        return error.message?.trim() || error.name || "Unknown error occurred.";
      } catch (messageError) {
        console.error(
          "Error extracting message from Error object:",
          messageError
        );
        return "Error object processing failed.";
      }
    }

    if (
      typeof error === "object" &&
      error !== null &&
      "reason" in error &&
      isNonEmptyString((error as { reason?: unknown }).reason)
    ) {
      try {
        const reason = String((error as { reason: unknown }).reason);
        return reason.trim() || "Empty reason provided.";
      } catch (reasonError) {
        console.error("Error processing reason property:", reasonError);
        return "Error reason processing failed.";
      }
    }

    // Handle other object types that might have a message property
    if (typeof error === "object" && error !== null && "message" in error) {
      try {
        const message = (error as { message?: unknown }).message;
        if (isNonEmptyString(message)) {
          return String(message).trim();
        }
        // Handle non-string message values
        return String(message || "No message provided");
      } catch (messageError) {
        console.error("Error processing message property:", messageError);
        return "Error message processing failed.";
      }
    }

    // Handle error objects with type property
    if (typeof error === "object" && error !== null && "type" in error) {
      try {
        const type = (error as { type?: unknown }).type;
        return `Zoom SDK Error: ${String(type || "Unknown type")}`;
      } catch (typeError) {
        console.error("Error processing type property:", typeError);
        return "Zoom SDK Error: Type processing failed.";
      }
    }

    // Handle error objects with code property
    if (typeof error === "object" && error !== null && "code" in error) {
      try {
        const code = (error as { code?: unknown }).code;
        return `Zoom SDK Error (Code: ${String(code || "Unknown")})`;
      } catch (codeError) {
        console.error("Error processing code property:", codeError);
        return "Zoom SDK Error: Code processing failed.";
      }
    }

    // Handle array errors
    if (Array.isArray(error)) {
      try {
        return `Zoom SDK Error: Array with ${error.length} elements`;
      } catch (arrayError) {
        console.error("Error processing array error:", arrayError);
        return "Zoom SDK Error: Array processing failed.";
      }
    }

    // Try to get any string-like property from the error object
    if (typeof error === "object" && error !== null) {
      try {
        const keys = Object.keys(error);
        for (const key of keys) {
          const value = (error as Record<string, unknown>)[key];
          if (isNonEmptyString(value)) {
            return `Zoom SDK Error (${key}): ${String(value).trim()}`;
          }
        }
      } catch (objectError) {
        console.error("Error processing error object properties:", objectError);
      }
    }

    // Final fallback - safe string conversion
    try {
      const errorString = String(error);
      return errorString.length > 100
        ? `Zoom SDK Error: ${errorString.substring(0, 100)}...`
        : `Zoom SDK Error: ${errorString}`;
    } catch (stringifyError) {
      console.error("Failed to convert error to string:", stringifyError);
      return `Zoom SDK Error: Unable to process error (type: ${typeof error})`;
    }
  } catch (processingError) {
    console.error("Critical error in normalizeZoomError:", processingError);
    return "Error processing Zoom SDK error message.";
  }
};

type PendingStatusInfo = {
  message?: string;
  metadata?: Record<string, unknown>;
} | null;

export default function App() {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const clientRef = useRef<ZoomClient | null>(null);
  const parentWindowRef = useRef<Window | null>(
    typeof window !== "undefined" && window.parent !== window
      ? window.parent
      : null
  );
  const parentOriginRef = useRef<string | null>(null);
  const defaultTargetOriginRef = useRef<string>(computeReferrerOrigin());
  const configurationRef = useRef<ZoomShellConfigurationPayload | null>(null);
  const statusAnnouncementRef = useRef<PendingStatusInfo>(null);
  const statusRef = useRef<ZoomShellStatus>("initializing");
  const hasEnteredMeetingRef = useRef<boolean>(false);
  const leaveResetTimeoutRef = useRef<number | null>(null);
  const connectionStatusPollRef = useRef<number | null>(null);
  const currentUserIdRef = useRef<string | null>(null);
  const manualLeaveDetectedRef = useRef<boolean>(false);
  const lastLeaveEventRef = useRef<{
    timestamp: number;
    userId?: string;
    source: string;
  } | null>(null);

  const [status, setStatus] = useState<ZoomShellStatus>("initializing");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);

  // Add error boundary for React errors
  const [reactError, setReactError] = useState<Error | null>(null);

  // Current user tracking for leave detection
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [manualLeaveDetected, setManualLeaveDetected] =
    useState<boolean>(false);
  const [lastLeaveEvent, setLastLeaveEvent] = useState<{
    timestamp: number;
    userId?: string;
    source: string;
  } | null>(null);

  useEffect(() => {
    currentUserIdRef.current = currentUserId;
  }, [currentUserId]);

  useEffect(() => {
    manualLeaveDetectedRef.current = manualLeaveDetected;
  }, [manualLeaveDetected]);

  useEffect(() => {
    lastLeaveEventRef.current = lastLeaveEvent;
  }, [lastLeaveEvent]);

  // Global error handler for unhandled errors
  useEffect(() => {
    const handleError = (event: ErrorEvent) => {
      console.error("[ZoomShell] Unhandled error:", event.error);
      const errorMsg = normalizeZoomError(event.error);
      setErrorMessage(`Unhandled error: ${errorMsg}`);
      setShellStatus("error", {
        message: errorMsg,
        metadata: {
          source: "global_error_handler",
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
        },
      });
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      console.error("[ZoomShell] Unhandled promise rejection:", event.reason);
      const errorMsg = normalizeZoomError(event.reason);
      setErrorMessage(`Unhandled promise rejection: ${errorMsg}`);
      setShellStatus("error", {
        message: errorMsg,
        metadata: {
          source: "promise_rejection_handler",
        },
      });
    };

    window.addEventListener("error", handleError);
    window.addEventListener("unhandledrejection", handleUnhandledRejection);

    return () => {
      window.removeEventListener("error", handleError);
      window.removeEventListener(
        "unhandledrejection",
        handleUnhandledRejection
      );
    };
  }, []);

  // Error boundary fallback
  if (reactError) {
    return (
      <div className="app-shell">
        <div className="app-shell__error" style={{ margin: "2rem" }}>
          <h3>React Error Boundary</h3>
          <p>
            <strong>Error:</strong> {reactError.message}
          </p>
          <p>
            <strong>Stack:</strong>
          </p>
          <pre style={{ fontSize: "0.8rem", overflow: "auto" }}>
            {reactError.stack}
          </pre>
          <button
            onClick={() => setReactError(null)}
            style={{ marginTop: "1rem", padding: "0.5rem 1rem" }}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const setShellStatus = useCallback(
    (nextStatus: ZoomShellStatus, info?: PendingStatusInfo) => {
      if (nextStatus === "in-meeting") {
        hasEnteredMeetingRef.current = true;
      }

      statusAnnouncementRef.current = info ?? null;
      statusRef.current = nextStatus;
      setStatus(nextStatus);
    },
    []
  );

  const clearConnectionStatusPoll = useCallback(() => {
    if (connectionStatusPollRef.current !== null) {
      window.clearTimeout(connectionStatusPollRef.current);
      connectionStatusPollRef.current = null;
    }
  }, []);

  const requestConnectionStatusPoll = useCallback(() => {
    clearConnectionStatusPoll();

    let attempts = 0;

    const probe = async () => {
      if (statusRef.current === "left" || statusRef.current === "error") {
        clearConnectionStatusPoll();
        return;
      }

      const isConnected = await new Promise<boolean>((resolve) => {
        try {
          ZoomMtg.getCurrentUser({
            success: () => resolve(true),
            error: () => resolve(false),
          });
        } catch {
          resolve(false);
        }
      });

      if (isConnected) {
        if (statusRef.current !== "in-meeting") {
          setShellStatus("in-meeting", {
            metadata: {
              event: "zoom_probe_connected",
              attempts,
            },
          });
        }

        clearConnectionStatusPoll();
        return;
      }

      attempts += 1;

      if (attempts > 120) {
        if (
          statusRef.current === "joining" ||
          statusRef.current === "joined"
        ) {
          const timeoutMessage =
            "Unable to connect to the meeting. Please try again.";

          setErrorMessage(timeoutMessage);
          setShellStatus("error", {
            message: timeoutMessage,
            metadata: {
              event: "zoom_join_timeout",
              attempts,
            },
          });
        }

        clearConnectionStatusPoll();
        return;
      }

      if (statusRef.current !== "joining") {
        clearConnectionStatusPoll();
        return;
      }

      connectionStatusPollRef.current = window.setTimeout(() => {
        void probe();
      }, 1000);
    };

    connectionStatusPollRef.current = window.setTimeout(() => {
      void probe();
    }, 1000);
  }, [clearConnectionStatusPoll, setShellStatus]);

  const clearLeaveResetTimeout = useCallback(() => {
    if (leaveResetTimeoutRef.current !== null) {
      window.clearTimeout(leaveResetTimeoutRef.current);
      leaveResetTimeoutRef.current = null;
    }
  }, []);

  const postToParent = useCallback(
    (
      payload:
        | ZoomShellStatusPayload
        | {
            code: string;
            message: string;
            recoverable?: boolean;
            data?: Record<string, unknown>;
          },
      type: "STATUS" | "ERROR"
    ) => {
      const targetWindow =
        parentWindowRef.current ??
        (typeof window !== "undefined" && window.parent !== window
          ? window.parent
          : null);

      if (!targetWindow) {
        return;
      }

      // For manual leave events, use "*" as fallback to ensure communication works
      // even if origin detection fails during iframe reload scenarios
      const isManualLeaveEvent =
        type === "STATUS" &&
        'metadata' in payload &&
        payload.metadata?.event === "zoom_meeting_leave";
      const targetOrigin = isManualLeaveEvent
        ? "*" // Use wildcard for critical manual leave events
        : (parentOriginRef.current ?? defaultTargetOriginRef.current ?? "*");

      if (type === "STATUS") {
        targetWindow.postMessage(
          createZoomShellEnvelope("STATUS", payload),
          targetOrigin
        );
      } else {
        targetWindow.postMessage(
          createZoomShellEnvelope("ERROR", payload),
          targetOrigin
        );
      }
    },
    []
  );

  // Type guard to validate ZoomShellStatus
  const isValidZoomShellStatus = (
    status: unknown
  ): status is ZoomShellStatus => {
    return (
      typeof status === "string" &&
      [
        "initializing",
        "ready",
        "joining",
        "joined",
        "in-meeting",
        "leaving",
        "left",
        "error",
      ].includes(status)
    );
  };

  const postStatus = useCallback(
    (nextStatus: ZoomShellStatus, info?: PendingStatusInfo) => {
      try {
        // Validate the status before processing
        if (!isValidZoomShellStatus(nextStatus)) {
          console.error("[ZoomShell] Invalid status received:", nextStatus);
          // Use "error" as fallback status
          nextStatus = "error";
        }

        const output: ZoomShellStatusPayload = {
          status: nextStatus,
          timestamp: Date.now(),
        };

        // Safely get default message with multiple fallbacks
        let defaultMessage: string;
        try {
          defaultMessage =
            STATUS_COPY[nextStatus] || String(nextStatus) || "Unknown status";
        } catch (error) {
          console.error("[ZoomShell] Error getting default message:", error);
          defaultMessage = "Status error";
        }

        // Safely assign message with fallback
        try {
          output.message = info?.message ?? defaultMessage;
          // Ensure message is a string
          if (typeof output.message !== "string") {
            output.message = String(output.message || defaultMessage);
          }
        } catch (error) {
          console.error("[ZoomShell] Error setting message:", error);
          output.message = defaultMessage;
        }

        // Safely handle metadata
        if (info?.metadata && Object.keys(info.metadata).length > 0) {
          try {
            output.metadata = info.metadata;
          } catch (error) {
            console.error("[ZoomShell] Error setting metadata:", error);
            output.metadata = { error: "Failed to process metadata" };
          }
        }

        postToParent(output, "STATUS");
      } catch (error) {
        console.error("[ZoomShell] Critical error in postStatus:", error);
        // Try to send a basic error status
        try {
          const errorOutput: ZoomShellStatusPayload = {
            status: "error",
            timestamp: Date.now(),
            message: "Error processing status update",
          };
          postToParent(errorOutput, "STATUS");
        } catch (criticalError) {
          console.error(
            "[ZoomShell] Failed to send error status:",
            criticalError
          );
        }
      }
    },
    [postToParent]
  );

  const postError = useCallback(
    (
      code: string,
      message: string,
      data?: Record<string, unknown>,
      recoverable = true
    ) => {
      try {
        // Validate inputs
        const safeCode = typeof code === "string" ? code : "UNKNOWN_ERROR";
        const safeMessage =
          typeof message === "string"
            ? message
            : String(message || "Unknown error occurred");

        const errorOutput = {
          code: safeCode,
          message: safeMessage,
          recoverable,
          data: data || {},
        };

        postToParent(errorOutput, "ERROR");
      } catch (error) {
        console.error("[ZoomShell] Critical error in postError:", error);
        // Last resort - try to send minimal error
        try {
          postToParent(
            {
              code: "POST_ERROR_FAILED",
              message: "Failed to post error message",
              recoverable: false,
            },
            "ERROR"
          );
        } catch (criticalError) {
          console.error(
            "[ZoomShell] Failed to send fallback error:",
            criticalError
          );
        }
      }
    },
    [postToParent]
  );

  const ensureClient = useCallback((): ZoomClient | null => {
    if (!clientRef.current) {
      clientRef.current = createZoomClient();
    }

    return clientRef.current;
  }, []);

  const ensureParentBinding = useCallback(
    (event: MessageEvent, command: ZoomShellCommandEnvelope) => {
      if (event.source && event.source instanceof Window) {
        if (
          parentWindowRef.current &&
          parentWindowRef.current !== event.source
        ) {
          return false;
        }

        parentWindowRef.current = event.source;
      }

      const allowedOrigins =
        configurationRef.current?.allowedOrigins ??
        (parentOriginRef.current ? [parentOriginRef.current] : undefined);

      const origin = event.origin === "null" ? "*" : event.origin;

      if (
        allowedOrigins &&
        allowedOrigins.length > 0 &&
        !allowedOrigins.includes(origin)
      ) {
        postError(
          "origin_not_allowed",
          "Blocked message from disallowed origin.",
          {
            origin,
            type: command.type,
          }
        );
        return false;
      }

      if (!parentOriginRef.current && origin && origin !== "null") {
        parentOriginRef.current = origin;
      }

      return true;
    },
    [postError]
  );

  const handleJoinCommand = useCallback(
    async (payload: ZoomJoinMeetingCommandPayload) => {
      if (statusRef.current === "joining" || statusRef.current === "joined") {
        postError(
          "invalid_state",
          "A meeting is already in progress.",
          { status: statusRef.current },
          true
        );
        return;
      }

      const client = ensureClient();

      if (!client) {
        postError(
          "client_unavailable",
          "Zoom client is not initialized.",
          undefined,
          false
        );
        setShellStatus("error", {
          message: "Failed to initialize Zoom client.",
          metadata: { scope: "join" },
        });
        setErrorMessage("Failed to initialize Zoom client.");
        return;
      }

      if (!isNonEmptyString(payload.signature)) {
        postError(
          "invalid_payload",
          "Missing signature for join command.",
          { payload },
          false
        );
        return;
      }

      if (!isNonEmptyString(payload.meetingNumber)) {
        postError(
          "invalid_payload",
          "Missing meeting number for join command.",
          { payload },
          false
        );
        return;
      }

      if (!isNonEmptyString(payload.userName)) {
        postError(
          "invalid_payload",
          "Missing user name for join command.",
          { payload },
          false
        );
        return;
      }

      clearLeaveResetTimeout();
      clearConnectionStatusPoll();
      hasEnteredMeetingRef.current = false;
      setCurrentUserId(null);
      currentUserIdRef.current = null;
      setManualLeaveDetected(false);
      manualLeaveDetectedRef.current = false;
      setLastLeaveEvent(null);
      lastLeaveEventRef.current = null;
      setShellStatus("joining", {
        metadata: { meetingNumber: payload.meetingNumber },
      });
      setErrorMessage(null);
      requestConnectionStatusPoll();

      try {
        await client.join({
          signature: payload.signature,
          meetingNumber: payload.meetingNumber,
          sdkKey: payload.sdkKey,
          password: payload.password,
          userName: payload.userName,
          userEmail: payload.userEmail,
          customerKey: payload.customerKey,
          tk: payload.tk,
          zak: payload.zak,
        });

        // Get and store current user ID for leave detection
        try {
          const currentUser = await new Promise<ZoomGetCurrentUserPayload>(
            (resolve, reject) => {
              ZoomMtg.getCurrentUser({
                success: (user: ZoomGetCurrentUserPayload) => resolve(user),
                error: (error: unknown) => reject(error),
              });
            }
          );

          if (currentUser?.userId) {
            setCurrentUserId(currentUser.userId);
            currentUserIdRef.current = currentUser.userId;
          }
        } catch (error) {
          console.warn("[ZoomShell] Could not get current user ID:", error);
        }

        setShellStatus("joined", {
          metadata: { meetingNumber: payload.meetingNumber },
        });
      } catch (error) {
        clearConnectionStatusPoll();
        console.log(JSON.stringify(error));
        const message = normalizeZoomError(error);
        setErrorMessage(message);
        setShellStatus("error", {
          message,
          metadata: { stage: "join", meetingNumber: payload.meetingNumber },
        });
        postError("join_failed", message, { payload }, true);
      }
    },
    [
      clearConnectionStatusPoll,
      clearLeaveResetTimeout,
      ensureClient,
      postError,
      requestConnectionStatusPoll,
      setShellStatus,
    ]
  );

  const handleLeaveMeeting = useCallback(
    async (terminate = false, reason?: string) => {
      const client = clientRef.current;

      if (!client) {
        postError(
          "client_unavailable",
          "Zoom client is not initialized.",
          undefined,
          false
        );
        return;
      }

      if (
        statusRef.current !== "joined" &&
        statusRef.current !== "in-meeting"
      ) {
        postError(
          "invalid_state",
          "No meeting is currently active.",
          { status: statusRef.current, reason },
          true
        );
        return;
      }

      clearConnectionStatusPoll();
      clearLeaveResetTimeout();
      setShellStatus("leaving", {
        metadata: { terminate, reason },
      });

      try {
        if (terminate) {
          await client.endMeeting();
        } else {
          await client.leaveMeeting();
        }

        setShellStatus("left", {
          metadata: { terminate, reason },
        });

        leaveResetTimeoutRef.current = window.setTimeout(() => {
          setShellStatus("ready");
          leaveResetTimeoutRef.current = null;
        }, 0);

        setErrorMessage(null);
      } catch (error) {
        const message = normalizeZoomError(error);
        setErrorMessage(message);
        setShellStatus("error", {
          message,
          metadata: { stage: "leave", terminate, reason },
        });
        postError("leave_failed", message, { terminate, reason }, true);
      }
    },
    [
      clearConnectionStatusPoll,
      clearLeaveResetTimeout,
      postError,
      setShellStatus,
    ]
  );

  const handleConfigure = useCallback(
    (payload: ZoomShellConfigurationPayload) => {
      configurationRef.current = {
        ...(configurationRef.current ?? {}),
        ...payload,
      };

      postStatus(statusRef.current, {
        metadata: {
          event: "configuration_updated",
          configuration: configurationRef.current,
        },
      });
    },
    [postStatus]
  );

  const handlePing = useCallback(
    (sentAt: number) => {
      postStatus(statusRef.current, {
        metadata: {
          event: "pong",
          sentAt,
          receivedAt: Date.now(),
        },
      });
    },
    [postStatus]
  );

  const handleCommandMessage = useCallback(
    (event: MessageEvent) => {
      try {
        if (!isZoomShellCommand(event.data)) {
          return;
        }

        const command: ZoomShellCommandEnvelope = event.data;

        // Validate command structure
        if (!command || typeof command.type !== "string") {
          console.error("[ZoomShell] Invalid command structure:", command);
          postError("invalid_command", "Received invalid command structure", {
            command,
          });
          return;
        }

        if (!ensureParentBinding(event, command)) {
          console.error("[ZoomShell] Parent binding failed");
          return;
        }

        // Validate payload exists for commands that need it
        if (command.type === "JOIN_MEETING" && !command.payload) {
          console.error("[ZoomShell] JOIN_MEETING command missing payload");
          postError(
            "missing_payload",
            "JOIN_MEETING command requires payload",
            { type: command.type }
          );
          return;
        }

        if (command.type === "CONFIGURE" && !command.payload) {
          console.error("[ZoomShell] CONFIGURE command missing payload");
          postError("missing_payload", "CONFIGURE command requires payload", {
            type: command.type,
          });
          return;
        }

        switch (command.type) {
          case "JOIN_MEETING":
            try {
              void handleJoinCommand(command.payload);
            } catch (error) {
              console.error("[ZoomShell] Error in handleJoinCommand:", error);
              postError(
                "join_command_failed",
                "Failed to process join command",
                { error }
              );
            }
            break;

          case "LEAVE_MEETING":
            try {
              void handleLeaveMeeting(false, command.payload?.reason);
            } catch (error) {
              console.error("[ZoomShell] Error in handleLeaveMeeting:", error);
              postError(
                "leave_command_failed",
                "Failed to process leave command",
                { error }
              );
            }
            break;

          case "END_MEETING":
            try {
              void handleLeaveMeeting(true, command.payload?.reason);
            } catch (error) {
              console.error(
                "[ZoomShell] Error in handleLeaveMeeting (END):",
                error
              );
              postError("end_command_failed", "Failed to process end command", {
                error,
              });
            }
            break;

          case "CONFIGURE":
            try {
              handleConfigure(command.payload);
            } catch (error) {
              console.error("[ZoomShell] Error in handleConfigure:", error);
              postError(
                "configure_command_failed",
                "Failed to process configure command",
                { error }
              );
            }
            break;

          case "PING":
            try {
              handlePing(command.payload?.timestamp);
            } catch (error) {
              console.error("[ZoomShell] Error in handlePing:", error);
              postError(
                "ping_command_failed",
                "Failed to process ping command",
                { error }
              );
            }
            break;

          default:
            console.warn(
              "[ZoomShell] Unknown command type:",
              (command as any).type
            );
            postError(
              "unknown_command",
              `Unknown command type: ${String((command as any).type)}`,
              { type: (command as any).type }
            );
            // Type assertion for unknown command type
            const unknownCommand = command as never;
            return unknownCommand;
        }
      } catch (error) {
        console.error(
          "[ZoomShell] Critical error in handleCommandMessage:",
          error
        );
        postError(
          "command_processing_failed",
          "Critical error processing command",
          { error }
        );
      }
    },
    [
      ensureParentBinding,
      handleConfigure,
      handleJoinCommand,
      handleLeaveMeeting,
      handlePing,
      postError,
    ]
  );

  if (!clientRef.current) {
    clientRef.current = createZoomClient();
  }

  useEffect(() => {
    const client = clientRef.current;

    // Prevent multiple initialization attempts
    if (isInitialized || !client) {
      return;
    }

    let cancelled = false;
    let initTimeout: number | null = null;

    const bootstrap = async () => {
      setShellStatus("initializing", {
        metadata: { event: "bootstrap_init" },
      });
      setErrorMessage(null);

      initTimeout = window.setTimeout(() => {
        if (!cancelled) {
          setErrorMessage("Initialization timed out. Please refresh the page.");
          setShellStatus("error", {
            message: "Initialization timeout",
            metadata: { stage: "init_timeout" },
          });
          postError(
            "init_timeout",
            "Zoom SDK initialization timed out",
            undefined,
            false
          );
        }
      }, 120000);

      const initOptions = {
        language: configurationRef.current?.language ?? "en-US",
        leaveUrl: configurationRef.current?.leaveUrl,
      };
      const MAX_INIT_RETRIES = 3;
      const INIT_RETRY_DELAY_MS = 3000;

      for (let attempt = 1; attempt <= MAX_INIT_RETRIES; attempt++) {
        if (cancelled) return;

        try {
          await client.init(initOptions);

          if (initTimeout) {
            window.clearTimeout(initTimeout);
            initTimeout = null;
          }

          if (!cancelled) {
            setIsInitialized(true);
            setShellStatus("ready", {
              metadata: { event: "bootstrap_ready" },
            });
          }
          return;
        } catch (error) {
          if (cancelled) return;

          if (attempt < MAX_INIT_RETRIES) {
            console.warn(
              `[ZoomShell] Init attempt ${attempt}/${MAX_INIT_RETRIES} failed, retrying in ${INIT_RETRY_DELAY_MS}ms...`
            );
            setErrorMessage(null);
            await new Promise((resolve) =>
              setTimeout(resolve, INIT_RETRY_DELAY_MS)
            );
            continue;
          }

          if (initTimeout) {
            window.clearTimeout(initTimeout);
            initTimeout = null;
          }

          const message = normalizeZoomError(error);
          setErrorMessage(message);
          setShellStatus("error", {
            message,
            metadata: { stage: "init" },
          });
          postError("init_failed", message, undefined, false);
        }
      }
    };

    void bootstrap();

    return () => {
      cancelled = true;
      if (initTimeout) {
        window.clearTimeout(initTimeout);
      }
    };
  }, [isInitialized]); // Only depend on isInitialized to prevent re-runs

  useEffect(() => {
    const listener = (event: MessageEvent) => {
      handleCommandMessage(event);
    };

    window.addEventListener("message", listener);
    return () => {
      window.removeEventListener("message", listener);
    };
  }, [handleCommandMessage]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const handleMeetingStatus = (payload: unknown) => {
      const statusSource =
        typeof payload === "object" && payload !== null && "status" in payload
          ? (payload as { status?: unknown }).status
          : payload;

      const nextStatus = mapMeetingStatus(statusSource);

      if (!nextStatus || statusRef.current === nextStatus) {
        return;
      }

      if (
        nextStatus === "joining" &&
        (statusRef.current === "joined" || statusRef.current === "in-meeting")
      ) {
        return;
      }

      if (nextStatus === "in-meeting") {
        clearConnectionStatusPoll();
      }

      // Enhanced logging for manual leave detection
      if (nextStatus === "left") {
        const currentStatus = statusRef.current;
        const hasEnteredMeeting = hasEnteredMeetingRef.current;
        const recentLeaveEvent = lastLeaveEventRef.current;
        const manualLeaveDetected = manualLeaveDetectedRef.current;
        const currentUserId = currentUserIdRef.current;
        const isFromInMeeting =
          currentStatus === "in-meeting" || currentStatus === "joined";

        // iPad Safari can emit transient "left" while camera/mic permission
        // prompts are being resolved. Ignore any "left" transition before
        // we have actually reached an in-meeting state.
        if (!hasEnteredMeeting && !manualLeaveDetected) {
          console.warn(
            "[ZoomShell] Ignoring pre-connection 'left' status.",
            {
              currentStatus,
              hasEnteredMeeting,
              statusSource,
              payload,
            }
          );
          return;
        }

        console.log("[ZoomShell] STATUS LEFT EVENT START:", {
          statusSource,
          nextStatus: "left",
          previousStatus: currentStatus,
          manualLeaveDetected,
          lastLeaveEvent: recentLeaveEvent,
          timestamp: Date.now(),
        });

        console.log("[ZoomShell] STATUS CHANGE TO 'LEFT':", {
          previousStatus: currentStatus,
          newStatus: nextStatus,
          originalPayload: payload,
          timestamp: Date.now(),
          meetingStatusSource: statusSource,
          isFromInMeeting,
          hasEnteredMeeting,
          manualLeaveDetected,
          lastLeaveEvent: recentLeaveEvent,
        });

        const leaveType =
          isFromInMeeting && manualLeaveDetected ? "manual" : "automatic";

        console.log("[ZoomShell] LEAVE TYPE DETERMINED:", {
          leaveType,
          isFromInMeeting,
          hasEnteredMeeting,
          manualLeaveDetected,
          willSendToParent: true,
        });

        setShellStatus(nextStatus, {
          metadata: {
            event: "zoom_meeting_leave",
            leaveType,
            previousStatus: currentStatus,
            timestamp: Date.now(),
            currentUserId,
            lastLeaveEvent: recentLeaveEvent,
            zoomStatus: statusSource,
          },
        });

        console.log(
          "[ZoomShell] STATUS WITH MANUAL LEAVE METADATA SENT TO PARENT:",
          {
            status: nextStatus,
            metadata: {
              event: "zoom_meeting_leave",
              leaveType,
              previousStatus: currentStatus,
              timestamp: Date.now(),
              currentUserId,
              lastLeaveEvent: recentLeaveEvent,
              zoomStatus: statusSource,
            },
          }
        );

        // Reset manual leave flag after processing
        setTimeout(() => {
          manualLeaveDetectedRef.current = false;
          setManualLeaveDetected(false);
        }, 1000);
      } else {
        setShellStatus(nextStatus, {
          metadata: {
            event: "zoom_meeting_status",
            zoomStatus: statusSource,
          },
        });
      }
    };

    try {
      ZoomMtg.inMeetingServiceListener("onMeetingStatus", handleMeetingStatus);
    } catch (error) {
      console.error(
        "[ZoomShell] Failed to register Zoom meeting status listener:",
        error
      );
    }

    // Add leave event listeners
    try {
      // Listen for specific user leave events - most reliable for manual leaves
      ZoomMtg.inMeetingServiceListener(
        "onUserLeave",
        (payload: ZoomUserLeavePayload) => {
          const timestamp = Date.now();
          const leaveEvent = {
            timestamp,
            userId: payload.userId || "unknown",
            source: "onUserLeave",
          };

          setLastLeaveEvent(leaveEvent);
          lastLeaveEventRef.current = leaveEvent;

          const currentUserId = currentUserIdRef.current;
          const currentStatus = statusRef.current;

          // Only treat this as manual leave when the participant was already
          // in a stable meeting state.
          const isCurrentUserLeave = payload.userId === currentUserId;
          const hasLeavingReason = payload.reason === 4; // Reason 4 = user left by themselves
          const isInMeetingState = currentStatus === "in-meeting";

          const shouldDetectManualLeave =
            isInMeetingState && (isCurrentUserLeave || hasLeavingReason);

          if (shouldDetectManualLeave) {
            manualLeaveDetectedRef.current = true;
            setManualLeaveDetected(true);

            // IMMEDIATE ACTION: Send manual leave status to parent immediately
            // so the host can react even if the SDK status callback is delayed.
            const manualLeaveMetadata = {
              event: "zoom_meeting_leave",
              leaveType: "manual",
              previousStatus: currentStatus,
              timestamp: Date.now(),
              currentUserId,
              lastLeaveEvent: leaveEvent,
              zoomStatus: "user_leave",
              detectionReason: {
                isCurrentUserLeave,
                hasLeavingReason,
                isInMeetingState,
              },
            };

            setShellStatus("left", {
              metadata: manualLeaveMetadata,
            });
          }
        }
      );

      // Listen for attendee leave events (provides more detailed info)
      ZoomMtg.inMeetingServiceListener(
        "onAttendeeLeave",
        (payload: ZoomAttendeeLeavePayload) => {
          // Attendee leave events for additional information if needed
        }
      );

      // Listen for all meeting events to capture leave-specific events
      ZoomMtg.inMeetingServiceListener(
        "onMeetingEvent",
        (payload: ZoomMeetingEventPayload) => {
          if (payload.event === "leave" || payload.type === "leave") {
            // Meeting leave events for additional tracking if needed
          }
        }
      );
    } catch (error) {
      console.error(
        "[ZoomShell] Failed to register Zoom leave event listeners:",
        error
      );
    }

    return () => {
      try {
        ZoomMtg.inMeetingServiceListener("onMeetingStatus", () => {});
        ZoomMtg.inMeetingServiceListener("onUserLeave", () => {});
        ZoomMtg.inMeetingServiceListener("onAttendeeLeave", () => {});
        ZoomMtg.inMeetingServiceListener("onMeetingEvent", () => {});
      } catch (error) {
        console.error(
          "[ZoomShell] Failed to clean up meeting event listeners:",
          error
        );
      }
    };
  }, [clearConnectionStatusPoll, setShellStatus]);

  useEffect(() => {
    const info = statusAnnouncementRef.current;
    postStatus(status, info ?? undefined);
    statusAnnouncementRef.current = null;

    // Sync ref with state to prevent drift
    if (statusRef.current !== status) {
      statusRef.current = status;
    }
  }, [postStatus, status]);

  useEffect(() => {
    return () => {
      clearLeaveResetTimeout();
      clearConnectionStatusPoll();

      const client = clientRef.current;

      if (!client) {
        destroyZoomClient();
        return;
      }

      void client.leaveMeeting().catch(() => {
        /* ignoring leave errors on teardown */
      });

      destroyZoomClient();
      // Don't null the client ref here - let it be naturally cleaned up
      // clientRef.current = null;
    };
  }, [clearConnectionStatusPoll, clearLeaveResetTimeout]);

  return (
    <div className="app-shell">
      <main className="app-shell__content">
        <div ref={containerRef} className="app-shell__zoom-root" />
        {status === "error" && errorMessage && (
          <div className="app-shell__error" role="alert">
            {errorMessage}
          </div>
        )}
      </main>
    </div>
  );
}
